import React, { useRef } from 'react';
import { Box, Button, Dialog, IconButton, Typography, useMediaQuery, useTheme } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LogoUploadIcon from '../../assets/icons/LogoUpload.svg';

const UploadPracticeLogoModal = ({
    open,
    onClose,
    onSave,
    onSelectFile,
    selectedFile,
    isSaving = false,
    title = 'Upload practice logo',
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const inputRef = useRef(null);

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="md"
            PaperProps={{
                sx: {
                    width: isMobile ? 354 : 895,
                    height: isMobile ? 317 : 637,
                    maxHeight: isMobile ? 317 : 637,
                    maxWidth: 'calc(100vw - 24px)',
                    borderRadius: isMobile ? '20px' : '30px',
                    p: isMobile ? '14px 14px 24px' : '31px 39px 39px',
                    boxSizing: 'border-box',
                    position: 'relative',
                },
            }}
        >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box>
                    <Typography
                        sx={{
                            color: '#000453',
                            fontFamily: 'Inter',
                            fontSize: isMobile ? '26px' : '42px',
                            fontStyle: 'normal',
                            fontWeight: 600,
                            lineHeight: isMobile ? '26px' : '42px',
                            letterSpacing: isMobile ? '-1.04px' : '-1.68px',
                        }}
                    >
                        {title}
                    </Typography>
                    <Typography
                        sx={{
                            mt: isMobile ? 0.5 : '24px',
                            color: '#616063',
                            fontFamily: 'Inter',
                            fontSize: isMobile ? '14px' : '24px',
                            fontStyle: 'normal',
                            fontWeight: 400,
                            lineHeight: isMobile ? '18px' : '24px',
                            letterSpacing: isMobile ? '-0.56px' : '-0.96px',
                        }}
                    >
                        Your practice logo will appear at the bottom of every handout
                    </Typography>
                </Box>
                <IconButton
                    onClick={onClose}
                    disableRipple
                    sx={{
                        p: 0,
                        width: isMobile ? 24 : '43.600px',
                        height: isMobile ? 24 : '43.600px',
                        borderRadius: 0,
                        backgroundColor: 'transparent',
                        '&:hover': { backgroundColor: 'transparent' },
                    }}
                >
                    <CloseIcon sx={{ color: '#616063', fontSize: isMobile ? 22 : '24.5px' }} />
                </IconButton>
            </Box>

            <Box
                sx={{
                    mt: isMobile ? '14px' : '60px',
                    border: isMobile ? '1px dashed #D7D7E0' : '2.21px dashed rgba(0, 4, 83, 0.05)',
                    background: isMobile ? '#F0F0FC' : 'rgba(240, 240, 252, 0.5)',
                    borderRadius: isMobile ? '8px' : '13.259px',
                    width: isMobile ? '100%' : '817px',
                    height: isMobile ? 127 : '287.556px',
                    boxSizing: 'border-box',
                    px: isMobile ? 2 : '53.037px',
                    py: isMobile ? 1.5 : '50px',
                    textAlign: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: isMobile ? 'flex-start' : 'center',
                    overflow: 'hidden',
                }}
                onDragOver={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                }}
                onDrop={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    const file = e.dataTransfer?.files?.[0];
                    if (file) onSelectFile(file);
                }}
            >
                <Box
                    component="img"
                    src={LogoUploadIcon}
                    alt="Upload logo"
                    sx={{
                        width: isMobile ? '29.408px' : '53.037px',
                        height: isMobile ? '29.408px' : '53.037px',
                        mb: isMobile ? '6px' : '26.519px',
                        flexShrink: 0,
                    }}
                />
                <Typography
                    sx={{
                        color: '#616063',
                        textAlign: 'center',
                        fontFamily: 'Inter',
                        fontSize: isMobile ? '14px' : '30px',
                        fontStyle: 'normal',
                        fontWeight: 500,
                        lineHeight: isMobile ? '14px' : '30px',
                        letterSpacing: isMobile ? '-0.28px' : '-0.6px',
                    }}
                >
                    {isMobile ? null : 'Drag & Drop or '}
                    <Box
                        component="span"
                        onClick={() => inputRef.current?.click()}
                        sx={{
                            color: '#FF669B',
                            textAlign: 'center',
                            fontFamily: 'Inter',
                            fontSize: isMobile ? '14px' : '30px',
                            fontStyle: 'normal',
                            fontWeight: 500,
                            lineHeight: isMobile ? '14px' : '30px',
                            letterSpacing: isMobile ? '-0.28px' : '-0.6px',
                            textDecorationLine: 'underline',
                            textDecorationStyle: 'solid',
                            textDecorationSkipInk: 'auto',
                            textDecorationThickness: 'auto',
                            textUnderlineOffset: 'auto',
                            textUnderlinePosition: 'from-font',
                            cursor: 'pointer',
                        }}
                    >
                        Choose a file
                    </Box>{' '}
                    to upload
                </Typography>
                <Typography
                    sx={{
                        mt: isMobile ? '6px' : '18px',
                        color: '#616063',
                        textAlign: 'center',
                        fontFamily: 'Inter',
                        fontSize: isMobile ? '12px' : '22px',
                        fontStyle: 'normal',
                        fontWeight: 400,
                        lineHeight: isMobile ? '16px' : '30px',
                        letterSpacing: isMobile ? '-0.24px' : '-0.44px',
                    }}
                >
                    Please ensure the image dimensions
                    <br />
                    do not exceed 700x700 pixels
                </Typography>
                {selectedFile && (
                    <Typography
                        sx={{
                            mt: isMobile ? '4px' : 1,
                            color: '#000453',
                            fontSize: isMobile ? 10 : 12,
                            lineHeight: isMobile ? '10px' : '14px',
                            maxWidth: '100%',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            flexShrink: 0,
                        }}
                    >
                        {selectedFile.name}
                    </Typography>
                )}
                <input
                    ref={inputRef}
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) onSelectFile(file);
                    }}
                />
            </Box>

            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    mt: isMobile ? '24px' : '64px',
                    ...(isMobile
                        ? {
                              position: 'absolute',
                              left: '14px',
                              right: '14px',
                              bottom: '24px',
                          }
                        : {}),
                }}
            >
                <Button
                    onClick={onSave}
                    disabled={!selectedFile || isSaving}
                    sx={{
                        width: isMobile ? '100%' : '210.914px',
                        height: isMobile ? 35 : '55.751px',
                        borderRadius: isMobile ? '100px' : '30px',
                        backgroundColor: '#FF669B',
                        color: '#FFF',
                        textTransform: 'none',
                        fontSize: isMobile ? 14 : '24px',
                        fontWeight: 500,
                        lineHeight: isMobile ? '14px' : '24px',
                        letterSpacing: isMobile ? '-0.28px' : '-0.96px',
                        '&:hover': { backgroundColor: '#e65588' },
                        '&.Mui-disabled': { backgroundColor: '#FF669B', opacity: 0.5, color: '#FFF' },
                    }}
                >
                    {isSaving ? 'Saving...' : 'Save'}
                </Button>
            </Box>
        </Dialog>
    );
};

export default UploadPracticeLogoModal;
